import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  ogType?: "website" | "article" | "product";
  canonicalPath?: string;
  noindex?: boolean;
}

const DEFAULT_SEO = {
  title: "3D Druck Bolz – Professioneller 3D-Druckservice",
  description:
    "3D-Druck aus Hofheim – STL/3MF hochladen, Sofort-Preis erhalten. PLA, PETG, ABS, TPU, PA6-CF. Bambu Lab Qualität.",
  keywords:
    "3D Druck, 3D Druckservice, PLA, PETG, ABS, TPU, ASA, PA6-GF, PA6-CF, Bambu Lab, STL drucken, Hofheim",
  ogImage: "https://3d-druck-bolz.de/assets/hero-bambu-lab.webp",
};

export const SEO = ({
  title,
  description,
  keywords,
  ogImage,
  ogType = "website",
  canonicalPath,
  noindex = false,
}: SEOProps) => {
  const location = useLocation();
  const fullTitle = title ? `${title} | 3D Druck Bolz` : DEFAULT_SEO.title;
  const fullDescription = description || DEFAULT_SEO.description;
  const fullKeywords = keywords || DEFAULT_SEO.keywords;
  const fullOgImage = ogImage
    ? ogImage.startsWith("http")
      ? ogImage
      : `https://3d-druck-bolz.de${ogImage}`
    : DEFAULT_SEO.ogImage;
  const canonicalUrl = `https://3d-druck-bolz.de${canonicalPath || location.pathname}`;

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={fullDescription} />
      <meta name="keywords" content={fullKeywords} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:site_name" content="3D Druck Bolz" />
      <meta property="og:locale" content="de_DE" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={fullOgImage} />
    </Helmet>
  );
};
